import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAPI } from "../../../contexts/APIContext";

// City Helpers sound: warm WebAudio tones for pops / sparkles / thuds / the
// finale, plus real ElevenLabs voices (via the backend /elevenlabs/quick-tts)
// for the narrator prompts and the soothing breathing cues, using the same
// pinned voice ids as PixleyIOS (CityHelpersVoice.swift). Clips are cached per
// (voice, line) so replays are instant; the browser's speechSynthesis steps in
// whenever the network TTS is unavailable so pre-readers can play solo. Mute is
// persisted so the choice survives a reload.

const STORAGE_KEY = "pixley_city_helpers_muted";

// Pinned ElevenLabs voice ids (match PixleyIOS CityHelpersVoice.swift).
const NARRATOR_VOICE_ID = "NoOVOzCQFLOvtsMoNcdT"; // Pixley storyteller
const SOOTHING_VOICE_ID = "GBv7mTt0atIp3Br8iCZE"; // Thomas — breathing cues

// Module-level so warmed clips survive component remounts within a session.
const clipCache = new Map(); // `${voiceId}::${text}` -> base64 mp3

const createAudioContext = () => {
  if (typeof window === "undefined") return null;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  return Ctx ? new Ctx() : null;
};

const readMuted = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

export const useCityHelpersSoundManager = () => {
  const [muted, setMuted] = useState(readMuted);
  const mutedRef = useRef(muted);
  const contextRef = useRef(null);
  const voicesRef = useRef([]);
  const { apiClient } = useAPI();
  const apiRef = useRef(apiClient);
  apiRef.current = apiClient;
  const audioRef = useRef(null); // currently-playing ElevenLabs clip
  const speakSeqRef = useRef(0); // bumps on every speak/stop so stale async TTS never plays

  useEffect(() => {
    mutedRef.current = muted;
  }, [muted]);

  // Cache the available voices so we can prefer a warm English one.
  useEffect(() => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return undefined;
    const load = () => {
      voicesRef.current = window.speechSynthesis.getVoices() || [];
    };
    load();
    window.speechSynthesis.addEventListener?.("voiceschanged", load);
    return () => window.speechSynthesis.removeEventListener?.("voiceschanged", load);
  }, []);

  const ensureAudio = useCallback(async () => {
    if (!contextRef.current) contextRef.current = createAudioContext();
    const ctx = contextRef.current;
    if (ctx && ctx.state === "suspended") {
      try {
        await ctx.resume();
      } catch {
        // Ignore — some browsers reject resume() outside a gesture.
      }
    }
    return ctx;
  }, []);

  // Play a little sequence of notes with a soft attack/decay envelope.
  const playNotes = useCallback(
    async (notes, { type = "sine", gain = 0.06, gap = 0.075 } = {}) => {
      if (mutedRef.current) return;
      const ctx = await ensureAudio();
      if (!ctx) return;
      notes.forEach((note, index) => {
        const frequency = typeof note === "number" ? note : note.f;
        const duration = typeof note === "number" ? 0.16 : note.d || 0.16;
        const start = ctx.currentTime + index * gap;
        const osc = ctx.createOscillator();
        const env = ctx.createGain();
        osc.type = type;
        osc.frequency.setValueAtTime(frequency, start);
        env.gain.setValueAtTime(0.0001, start);
        env.gain.exponentialRampToValueAtTime(gain, start + 0.02);
        env.gain.exponentialRampToValueAtTime(0.0001, start + duration);
        osc.connect(env);
        env.connect(ctx.destination);
        osc.start(start);
        osc.stop(start + duration + 0.02);
      });
    },
    [ensureAudio]
  );

  const playPop = useCallback(
    (pitch = 0.5) => {
      const base = 520 + Math.max(0, Math.min(1, pitch)) * 360;
      playNotes([base, base * 1.25], { type: "triangle", gain: 0.07, gap: 0.05 });
    },
    [playNotes]
  );

  const playSparkle = useCallback(
    () => playNotes([880, 1175, 1568], { type: "triangle", gain: 0.045, gap: 0.05 }),
    [playNotes]
  );

  const playThud = useCallback(
    () => playNotes([180, 140], { type: "sine", gain: 0.08, gap: 0.05 }),
    [playNotes]
  );

  const playCelebrate = useCallback(
    () =>
      playNotes(
        [
          { f: 523, d: 0.2 },
          { f: 659, d: 0.2 },
          { f: 784, d: 0.2 },
          { f: 1047, d: 0.34 },
        ],
        { type: "triangle", gain: 0.07, gap: 0.11 }
      ),
    [playNotes]
  );

  const pickVoice = useCallback(() => {
    const voices = voicesRef.current;
    if (!voices || !voices.length) return null;
    return (
      voices.find((v) => /en(-|_)(US|GB)/i.test(v.lang) && /female|samantha|karen|zira|google/i.test(v.name)) ||
      voices.find((v) => /^en/i.test(v.lang)) ||
      voices[0]
    );
  }, []);

  const utter = useCallback(
    (text, { rate = 1, pitch = 1 } = {}) => {
      if (mutedRef.current || !text) return;
      if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
      try {
        window.speechSynthesis.cancel();
        const u = new window.SpeechSynthesisUtterance(text);
        const voice = pickVoice();
        if (voice) u.voice = voice;
        u.lang = voice?.lang || "en-US";
        u.rate = rate;
        u.pitch = pitch;
        u.volume = 1;
        window.speechSynthesis.speak(u);
      } catch {
        // Speech is a nice-to-have; never let it break play.
      }
    },
    [pickVoice]
  );

  const stopAudioClip = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  }, []);

  // Speak a line in a pinned ElevenLabs voice, falling back to the on-device
  // synth (`utter`) on a cold cache or any network failure so play never stalls.
  const speakVoiced = useCallback(
    (text, voiceId, fallbackProfile) => {
      if (mutedRef.current || !text) return;
      stopAudioClip();
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
      // Claim this request. Any earlier in-flight TTS (e.g. StrictMode's double
      // effect, or a rapid re-tap) is now stale and must not reach the speaker.
      const seq = (speakSeqRef.current += 1);
      const isCurrent = () => seq === speakSeqRef.current;
      const api = apiRef.current;
      if (!api?.quickTTS) {
        utter(text, fallbackProfile);
        return;
      }
      const playBase64 = (b64) => {
        if (mutedRef.current || !b64 || !isCurrent()) return;
        try {
          const audio = new window.Audio(`data:audio/mpeg;base64,${b64}`);
          audioRef.current = audio;
          audio.play().catch(() => isCurrent() && utter(text, fallbackProfile));
        } catch {
          if (isCurrent()) utter(text, fallbackProfile);
        }
      };
      const key = `${voiceId}::${text}`;
      const cached = clipCache.get(key);
      if (cached) {
        playBase64(cached);
        return;
      }
      api
        .quickTTS(text, voiceId)
        .then((b64) => {
          if (!isCurrent()) return;
          if (!b64) {
            utter(text, fallbackProfile);
            return;
          }
          clipCache.set(key, b64);
          playBase64(b64);
        })
        .catch(() => isCurrent() && utter(text, fallbackProfile));
    },
    [utter, stopAudioClip]
  );

  // Bright, friendly narrator for prompts and takeaways.
  const speak = useCallback(
    (text) => speakVoiced(text, NARRATOR_VOICE_ID, { rate: 0.98, pitch: 1.12 }),
    [speakVoiced]
  );
  // Slow, low and calm for the breathing cues.
  const speakSoothing = useCallback(
    (text) => speakVoiced(text, SOOTHING_VOICE_ID, { rate: 0.78, pitch: 0.92 }),
    [speakVoiced]
  );

  const stop = useCallback(() => {
    speakSeqRef.current += 1; // invalidate any in-flight TTS so it never plays
    stopAudioClip();
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      try {
        window.speechSynthesis.cancel();
      } catch {
        // no-op
      }
    }
  }, [stopAudioClip]);

  const toggleMute = useCallback(() => {
    setMuted((current) => {
      const next = !current;
      if (next) {
        speakSeqRef.current += 1; // muting invalidates any in-flight TTS
        stopAudioClip();
        if (typeof window !== "undefined" && "speechSynthesis" in window) {
          window.speechSynthesis.cancel();
        }
      }
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // ignore storage failures
      }
      return next;
    });
  }, [stopAudioClip]);

  useEffect(() => () => stop(), [stop]);

  return useMemo(
    () => ({
      muted,
      toggleMute,
      playPop,
      playSparkle,
      playThud,
      playCelebrate,
      speak,
      speakSoothing,
      stop,
    }),
    [muted, toggleMute, playPop, playSparkle, playThud, playCelebrate, speak, speakSoothing, stop]
  );
};
